import moment from 'moment';
import _ from 'lodash';

class MetricsController {
  constructor($scope, $rootScope, AuthenticationService, TimesheetMetricsService, $http, LeaveService, TimesheetService, $state) {
    "ngInject";
    this.metricsService = TimesheetMetricsService
    this.leaveService = LeaveService;
    this.timesheetService = TimesheetService;
    this.$state = $state;
    this.isTimesheetLoaded = false;
    let self = this;

    // Cache the gauge thresholds object to prevent infinite digest loops
    // Store as a property instead of creating a new object each time
    this.gaugeThresholds = {
      0: { color: '#e74c3c' },    // Red for 0-25%
      25: { color: '#9b59b6' },   // Violet for 25-50%
      50: { color: '#f39c12' },   // Orange for 50-75%
      75: { color: '#27ae60' }    // Green for 75-100%
    };

    // Thresholds for leave balance gauge
    // The ng-gauge component calculates percentage as (value / max) * 100
    // Thresholds are applied based on percentage ranges
    // For leave taken: lower percentage = more remaining (green), higher = less remaining (red)
    this.leaveGaugeThresholds = {
      0: { color: '#27ae60' },    // Green for 0-30% taken (plenty remaining)
      50: { color: '#f1c40f' },    // Violet for 30-60% taken (moderate remaining)
      65: { color: '#orange' },    // Orange for 60-85% taken (low remaining)
      85: { color: '#e74c3c' }     // Red for 85-100% taken (very low remaining)
    };

    // Initialize leave stats
    this.leaveStatsByType = {};

    $http.get($rootScope.apiHost+"/api/timesheets/metrics/hoursbychargecode",{withCredentials: true} ).then(
      function(response) {
        self.hoursByChargeCode = response.data;
      });

    self.metricsService.loadMetrics(moment().year());

    // Refresh leave data
    this.leaveService.refreshPto();

    // Watch for leaveTypes changes and calculate stats for each leave type
    $scope.$watch(() => this.leaveService.leaveTypes, (newVal) => {
      if (!newVal || newVal.length === 0) {
        this.leaveStatsByType = {};
        return;
      }

      // Calculate stats for each leave type
      const currentYear = moment().year();
      this.leaveStatsByType = {};
      newVal.forEach((leaveType) => {
        // Update leave diffs based on current timesheet if available
        if (this.timesheetService.currentTimesheet) {
          this.leaveService.updateTimesheetLeaveDiffs(
            this.timesheetService.currentTimesheet,
            this.timesheetService.persistedTimesheet
          );
        }
        this.leaveStatsByType[leaveType.leaveTypeName] = this.leaveService.calculateLeaveTypePtoStats(leaveType, currentYear);
      });
    });

    // Watch for timesheet changes to update leave diffs
    $scope.$watch(() => this.timesheetService.currentTimesheet, (newVal, oldVal) => {
      if (!newVal && !oldVal) return;

      // Update leave diffs based on current timesheet
      this.leaveService.updateTimesheetLeaveDiffs(newVal, this.timesheetService.persistedTimesheet);

      // Recalculate stats if we have leave types
      if (!this.leaveService.leaveTypes || this.leaveService.leaveTypes.length === 0) return;
      
      const currentYear = moment().year();
      this.leaveStatsByType = {};
      this.leaveService.leaveTypes.forEach((leaveType) => {
        this.leaveStatsByType[leaveType.leaveTypeName] = this.leaveService.calculateLeaveTypePtoStats(leaveType, currentYear);
      });
    }, true);

    $scope.$watch(() => this.currentTimesheet, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        console.log('currentTimesheet changed:', newVal);
        // Any additional logic needed when timesheet changes
      }
    });

  }


  getLeaveMaxBalance(leaveType) {
    return parseFloat(this.getLeaveStats(leaveType).accruedBalance) + parseFloat(this.getLeaveStats(leaveType).prevYearBalance);
  }

  getLeaveTakenPercentage(leaveType) {
    const stats = this.getLeaveStats(leaveType);
    if (!stats) return 0;
    
    const maxBalance = this.getLeaveMaxBalance(leaveType);
    const totalLeaveTaken = parseFloat(stats.totalLeaveTaken) || 0;
    
    if (maxBalance === 0) return 0;
    
    // Calculate percentage of leave taken (0-100)
    const percentage = (totalLeaveTaken / maxBalance) * 100;
    
    // Cap at 100% if taken exceeds max (shouldn't happen, but safety check)
    return Math.min(100, Math.max(0, percentage));
  }

  getTimesheetMonth() {
    if (!this.currentTimesheet) {
      return '';
    }

    return moment(this.currentTimesheet.endDate).format('MMMM')
  }

  getHoursForMonth() {
    if (!this.currentTimesheet) {
      return 0;
    }

    let month = this.getTimesheetMonth().toUpperCase();
    if(this.metricsService.metrics[month]) {
      let ch = this.metricsService.metrics[month].chargedHours;
      if(!ch || ch == null) {
        ch = 0;
      }

      return ch;
    }
  }

  getExpectedHoursForMonth() {
    if (!this.currentTimesheet) {
      return 0;
    }

    let month = this.getTimesheetMonth().toUpperCase()
    if(this.metricsService.metrics[month]) {
      return this.metricsService.metrics[month].expectedHours || 0;
    }

    return 0;
  }

  getTimesheetYear() {
    if (!this.currentTimesheet) {
      return moment().year();
    }

    return moment(this.currentTimesheet.endDate).format('YYYY')
  }

  getHoursForYear() {
    // return this.metricsService.yearlyChargedHours + this.timesheetsService.deltaChargeHoursFromPersisted;
    return this.metricsService.yearlyChargedHours;

  }

  getExpectedHoursForYear() {
    return this.metricsService.yearlyExpectedHours || 0;
  }

  getTotalHoursForChargeCodes() {
    if (!this.hoursByChargeCode || this.hoursByChargeCode.length === 0) {
      return 0;
    }
    return this.hoursByChargeCode.reduce((total, cc) => total + (parseFloat(cc.hours) || 0), 0);
  }

  getChargeCodePercentage(chargeCode) {
    const expectedHours = this.getExpectedHoursForYear();
    if (expectedHours === 0) return 0;
    return (parseFloat(chargeCode.hours) || 0) / expectedHours * 100;
  }

  getRemainingHoursPercentage() {
    const totalHours = this.getTotalHoursForChargeCodes();
    const expectedHours = this.getExpectedHoursForYear();
    if (expectedHours === 0) return 0;
    const remaining = Math.max(0, expectedHours - totalHours);
    return (remaining / expectedHours) * 100;
  }

  getChargeCodeColor(index) {
    // Generate colors for each segment - using a color palette
    const colors = [
      '#3498db', // Blue
      '#27ae60', // Green
      '#e74c3c', // Red
      '#f39c12', // Orange
      '#9b59b6', // Purple
      '#1abc9c', // Turquoise
      '#34495e', // Dark blue-gray
      '#e67e22', // Dark orange
      '#16a085', // Dark turquoise
      '#c0392b'  // Dark red
    ];
    return colors[index % colors.length];
  }

  getLeaveTypes() {
    return this.leaveService.leaveTypes || [];
  }

  getLeaveStats(leaveType) {
    if (!leaveType || !this.leaveStatsByType) {
      return null;
    }
    return this.leaveStatsByType[leaveType.leaveTypeName] || null;
  }

  getLeaveBalancePercentage(leaveType) {
    const stats = this.getLeaveStats(leaveType);
    if (!stats) return 0;
    
    // Calculate percentage based on starting balance + accrued balance as the maximum
    const maxBalance = parseFloat(stats.prevYearBalance) + parseFloat(stats.accruedBalance);
    const currentBalance = parseFloat(stats.balance);
    
    if (maxBalance === 0) return 0;
    
    // Return percentage, but cap at 100% if balance exceeds max (shouldn't happen, but safety check)
    return Math.min(100, Math.max(0, (currentBalance / maxBalance) * 100));
  }

  getLeaveBalanceTotal(leaveType) {
    const stats = this.getLeaveStats(leaveType);
    if (!stats) return 100; // Default to 100 if no stats
    
    // Use starting balance + accrued balance as the maximum for the gauge
    // Add some buffer to handle cases where balance might exceed this
    const total = parseFloat(stats.prevYearBalance) + parseFloat(stats.accruedBalance);
    return Math.max(100, total * 1.2); // Add 20% buffer, minimum 100
  }

  getLeaveUsedAmount(leaveType) {
    const stats = this.getLeaveStats(leaveType);
    if (!stats) return 0;
    
    // Calculate how much leave has been used
    // Used = Starting + Accrued - Current Balance
    const starting = parseFloat(stats.prevYearBalance) || 0;
    const accrued = parseFloat(stats.accruedBalance) || 0;
    const balance = parseFloat(stats.balance) || 0;
    
    const totalAvailable = starting + accrued;
    const used = totalAvailable - balance;
    
    // Return the used amount (will be the value for the gauge)
    // When balance is 0, used = totalAvailable (gauge full/shaded)
    // When balance is totalAvailable, used = 0 (gauge empty/grey)
    return Math.max(0, used);
  }

  navigateToLeavePage() {
    this.$state.go('leave');
  }

  }

  export default MetricsController;



// WEBPACK FOOTER //
// ./client/pages/timesheet/components/metrics/metrics.controller.js